import { Component, type ErrorInfo, type ReactNode } from "react";
import { RefreshCw, ServerCrash } from "lucide-react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Last-resort catch for render errors anywhere beneath it — mounted once
 * around the whole app (src/App.tsx), not per-feature, since its job is
 * "the app doesn't crash to a blank white screen," not handling any one
 * feature's failure modes. A class component because
 * `componentDidCatch`/`getDerivedStateFromError` have no hook equivalent.
 *
 * The fallback follows docs/ui/design-system.md §18's 500 state: copy that's
 * explicit this isn't the user's fault, plus a "Try again" action, instead
 * of a raw stack trace or a dead end.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Unhandled render error caught by ErrorBoundary:", error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <ServerCrash className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
        <div className="space-y-1">
          <h1 className="text-h1 text-foreground">Something went wrong</h1>
          <p className="max-w-sm text-body text-muted-foreground">
            This isn&rsquo;t something you did — an unexpected error interrupted this page. Try
            again, and if it keeps happening, let us know.
          </p>
        </div>
        <button
          type="button"
          onClick={this.handleRetry}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-button text-primary-foreground hover:bg-primary/90"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Try again
        </button>
      </div>
    );
  }
}
